import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppLayout from "../components/AppLayout";
import LogShotDialog from "../components/LogShotDialog";
import OnboardingDialog from "../components/OnboardingDialog";
import RestockAlerts from "../components/RestockAlerts";
import NutritionSummaryStrip from "../components/NutritionSummaryStrip";
import WeightInsightsCard from "../components/WeightInsightsCard";
import WeeklyInsightsCard from "../components/WeeklyInsightsCard";
import SupporterBadge from "../components/SupporterBadge";
import SupplementCompletionCard from "../components/SupplementCompletionCard";
import FindCoachBanner from "../components/FindCoachBanner";
import ClientOnboardingWizard from "../components/ClientOnboardingWizard";
import MentorshipStrip from "../components/MentorshipStrip";
import { useSettings } from "../context/SettingsContext";
import ChartFrame from "../components/ChartFrame";
import PageHero from "../components/PageHero";
import { useSiteTime } from "../lib/time";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Link } from "react-router-dom";
import { Plus, Flame, Layers, TrendingUp, Clock, Calculator, BookOpen, Package, Repeat, Scale, Check, X, Target, History as HistoryIcon, Zap, StopCircle, PlayCircle, Pause } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, XAxis, YAxis } from "recharts";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import ProductTour from "../components/ProductTour";

const TOUR_STORAGE_KEY = "amino_tour_seen_v1";

const TOUR_STEPS = [
  {
    placement: "center",
    title: "Welcome to The Amino Exchange",
    body: "Quick 90-second tour of your Command Deck. Use ← → to navigate, Esc to skip. Ready?",
  },
  {
    selector: '[data-testid="countdown-hero"]',
    placement: "bottom",
    title: "Next-Dose Countdown",
    body: "Your master timer. Counts down to your next scheduled dose based on your default interval + last logged shot. Turns red when overdue.",
  },
  {
    selector: '[data-testid="log-shot-button"]',
    placement: "bottom",
    title: "Log a Shot",
    body: "Tap here (or the floating gold “+” anywhere in the app) to log a new dose. Pick peptide, dose, injection site on the body diagram, and time.",
  },
  {
    selector: '[data-testid="peptide-timers-section"]',
    placement: "top",
    title: "Per-Peptide Timers",
    body: "Each active peptide gets its own countdown. Tap a card to log the next dose for that peptide in one click.",
  },
  {
    selector: '[data-testid="body-weight-card"]',
    placement: "bottom",
    title: "Body Weight",
    body: "Log today's weight inline. Set a goal in Profile and this card shows distance-to-goal + a 7-day trend line.",
  },
  {
    selector: '[data-testid="quick-actions"]',
    placement: "top",
    title: "Quick Actions",
    body: "Jump into Calculator, Inventory, Library, or Cycles without menu-diving.",
  },
  {
    selector: '[data-testid="nav-tutorial"]',
    placement: "bottom",
    title: "Full Written Tutorial",
    body: "For deep-dives on any feature, hit the Tutorial tab in the Dashboard dropdown. Searchable, always up-to-date.",
  },
  {
    placement: "center",
    title: "You're all set",
    body: "You can retake this tour anytime by tapping “Take the tour” on the Dashboard header. Now — log your first shot!",
  },
];

function useCountdown(target) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!target) return null;
  const diff = new Date(target).getTime() - now;
  const overdue = diff <= 0;
  const abs = Math.abs(diff);
  const hours = Math.floor(abs / 3600000);
  const minutes = Math.floor((abs % 3600000) / 60000);
  const seconds = Math.floor((abs % 60000) / 1000);
  return { overdue, hours, minutes, seconds, diff };
}

function fmtDateTime(iso, tz) {
  if (!iso) return "—";
  const d = new Date(iso);
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  }).format(d);
}

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const { tz } = useSiteTime();
  const { settings } = useSettings();
  const coachingEnabled = (settings?.feature_flags?.coaching ?? true) !== false;
  const [stats, setStats] = useState(null);
  const [peptides, setPeptides] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [shots, setShots] = useState([]);
  const [vials, setVials] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [quickLogPeptide, setQuickLogPeptide] = useState(null);
  const [weightEdit, setWeightEdit] = useState(false);
  const [weightVal, setWeightVal] = useState("");
  const [goalEdit, setGoalEdit] = useState(false);
  const [goalVal, setGoalVal] = useState("");
  const [now, setNow] = useState(() => Date.now());
  const [tourOpen, setTourOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [onboardingInitial, setOnboardingInitial] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const loadedRef = useRef(false);

  // Deep-link support: opening /dashboard?logshot=1 (from the mobile FAB or any
  // other entry point) should auto-open the Log Shot dialog and immediately
  // strip the query so a refresh doesn't re-trigger it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("logshot") === "1") {
      setOpenDialog(true);
      params.delete("logshot");
      const qs = params.toString();
      navigate({ pathname: location.pathname, search: qs ? `?${qs}` : "" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Auto-open the client onboarding wizard once per user — but only after
  // ToS acceptance so it doesn't stack under the terms modal.
  useEffect(() => {
    if (!user) return;
    if (user.terms_accepted === false) return;
    let cancelled = false;
    api.get("/client/onboarding-status").then(({ data }) => {
      if (cancelled) return;
      if (data && data.onboarded === false) {
        setOnboardingInitial(data);
        setOnboardingOpen(true);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id, user?.terms_accepted]);

  // Open the tour when arriving with ?tour=1 (from the global "?" help button)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tour") === "1") {
      setTourOpen(true);
      // Strip the param so a page refresh doesn't reopen the tour
      params.delete("tour");
      navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  // First-run auto-launch of the product tour — suppressed while the
  // client onboarding wizard is open so we never stack modals.
  useEffect(() => {
    if (onboardingOpen) return;
    try {
      if (!localStorage.getItem(TOUR_STORAGE_KEY)) {
        // small delay so the dashboard mounts + real data lands
        const t = setTimeout(() => setTourOpen(true), 900);
        return () => clearTimeout(t);
      }
    } catch { /* localStorage disabled — silently skip auto-launch */ }
  }, [onboardingOpen]);

  const closeTour = useCallback(() => {
    setTourOpen(false);
    try { localStorage.setItem(TOUR_STORAGE_KEY, "1"); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    try {
      const [statsRes, pepRes, cyclesRes, shotsRes, vialsRes] = await Promise.all([
        api.get("/stats"),
        api.get("/peptides"),
        api.get("/cycles"),
        api.get("/shots"),
        api.get("/vials"),
      ]);
      setStats(statsRes.data);
      setPeptides(pepRes.data);
      setCycles(cyclesRes.data);
      setShots(shotsRes.data);
      setVials(vialsRes.data);
      loadedRef.current = true;
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Dose reminder — fire browser notification when next_dose_at hits (if user opted in and permission granted)
  useEffect(() => {
    if (!user?.notifications_enabled) return;
    if (!stats?.next_dose_at) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const target = new Date(stats.next_dose_at).getTime();
    const now = Date.now();
    const delay = target - now;
    if (delay <= 0 || delay > 24 * 3600 * 1000) return;
    const notifiedKey = `amino_notified_${target}`;
    if (localStorage.getItem(notifiedKey)) return;

    // Prefer service worker so notifications fire even if tab is backgrounded
    const body = stats?.last_shot?.peptide_name
      ? `Time for your ${stats.last_shot.peptide_name} shot.`
      : "Time for your next shot.";
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      try {
        navigator.serviceWorker.controller.postMessage({
          type: "schedule-dose",
          fireAt: new Date(target).toISOString(),
          title: "The Amino Exchange — Dose Due",
          body,
          tag: `amino-dose-${target}`,
        });
        localStorage.setItem(notifiedKey, "1");
        return;
      } catch { /* fall through to setTimeout */ }
    }
    const timer = setTimeout(() => {
      try {
        new Notification("The Amino Exchange — Dose Due", {
          body,
          icon: "/icon-192.png",
          tag: "amino-next-dose",
        });
        localStorage.setItem(notifiedKey, "1");
      } catch { /* ignore */ }
    }, delay);
    return () => clearTimeout(timer);
  }, [user?.notifications_enabled, stats]);

  const saveWeight = async () => {
    const val = parseFloat(weightVal);
    if (isNaN(val) || val <= 0) { toast.error("Enter a valid weight"); return; }
    try {
      await api.patch("/users/me", { body_weight: val });
      await refreshUser();
      setWeightEdit(false);
      toast.success("Weight updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveGoal = async () => {
    const val = goalVal === "" ? null : parseFloat(goalVal);
    if (val !== null && (isNaN(val) || val <= 0)) { toast.error("Enter a valid goal weight"); return; }
    try {
      await api.patch("/users/me", { goal_weight: val });
      await refreshUser();
      setGoalEdit(false);
      toast.success("Goal updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const activeCycles = cycles.filter((c) => c.active);

  const countdown = useCountdown(stats?.next_dose_at);

  const handleEndCountdown = async (peptideName) => {
    try {
      await api.post("/peptide-status/end", { peptide_name: peptideName });
      toast.success(`Countdown ended for ${peptideName}. History kept.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleReactivate = async (peptideName) => {
    try {
      await api.post("/peptide-status/reactivate", { peptide_name: peptideName });
      toast.success(`Reactivated ${peptideName} — log a shot to start the schedule.`);
      // Seed the Log Shot dialog so the user can start the new schedule in one flow.
      setQuickLogPeptide(peptideName);
      setOpenDialog(true);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const chartData = useMemo(() => {
    if (!stats?.chart) return [];
    return stats.chart.map((c) => ({
      day: new Intl.DateTimeFormat(undefined, { timeZone: tz, weekday: "short" }).format(new Date(c.date)),
      shots: c.count,
    }));
  }, [stats]);

  return (
    <AppLayout>
      {/* Nutrition summary — read-only tiles linking to /nutrition */}
      <PageHero
        eyebrow={`Welcome back, ${user?.name?.split(" ")[0] || "operator"}`}
        title="Command Deck"
        subtitle="Your next dose, active protocols, and today's fuel — one glance."
        icon={<Zap className="w-8 h-8 text-[#0D9488]" />}
        gradient
        actions={
          <div className="flex items-center gap-3 flex-wrap">
            <SupporterBadge />
            <Button
              onClick={() => setOpenDialog(true)}
              className="bg-[#0D9488] hover:bg-[#0F766E] rounded-none h-11 px-6 uppercase tracking-widest font-semibold text-black"
              data-testid="log-shot-button"
            >
              <Plus className="w-4 h-4 mr-2" /> Log Shot
            </Button>
          </div>
        }
      />

      <div className="mb-6">
        <WeeklyInsightsCard />
      </div>

      <div className="mb-6">
        <NutritionSummaryStrip />
      </div>

      {/* Weight Insights — 30-day trend + predicted goal date */}
      <div className="mb-6 grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-6">
        <WeightInsightsCard />
        <SupplementCompletionCard />
      </div>

      {/* Find a Coach — compact banner linking to the full /coaches directory */}
      {coachingEnabled && (
        <div className="mb-6">
          <FindCoachBanner />
        </div>
      )}

      {/* Grid: hero countdown + stat rail */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Countdown hero */}
        <Card className="col-span-1 lg:col-span-3 relative bg-white border border-slate-200 rounded-none p-8 lg:p-10 grid-dots overflow-hidden" data-testid="countdown-hero">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-2 h-2 bg-[#0D9488] animate-pulse" />
            <span className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500">
              // {countdown?.overdue ? "Overdue — take next dose" : "Next dose in"}
            </span>
          </div>

          {stats?.next_dose_at ? (
            <div className="relative z-10">
              <div className={`font-display font-black leading-[0.85] tracking-tight text-6xl md:text-7xl lg:text-[128px] ${countdown?.overdue ? "text-[#FF3B30]" : "text-slate-900"}`} data-testid="countdown-timer-display">
                <span className="font-mono-num">{String(countdown?.hours ?? 0).padStart(2, "0")}</span>
                <span className="text-slate-400 mx-2">:</span>
                <span className="font-mono-num">{String(countdown?.minutes ?? 0).padStart(2, "0")}</span>
                <span className="text-slate-400 mx-2">:</span>
                <span className="font-mono-num">{String(countdown?.seconds ?? 0).padStart(2, "0")}</span>
              </div>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-8 pt-6 border-t border-slate-200">
                <InfoLine label="Target" value={fmtDateTime(stats.next_dose_at, tz)} />
                <InfoLine label="Last dose" value={fmtDateTime(stats?.last_shot?.taken_at, tz)} />
                <InfoLine
                  label="Compound"
                  value={stats?.last_shot ? `${stats.last_shot.peptide_name} · ${stats.last_shot.dose_amount}${stats.last_shot.dose_unit}` : "—"}
                />
              </div>
            </div>
          ) : (
            <div className="py-6">
              <div className="font-display font-black text-4xl lg:text-5xl uppercase tracking-tight text-slate-500">No shots logged yet</div>
              <p className="text-slate-500 mt-3 max-w-md">Log your first dose to activate the countdown timer and begin tracking your protocol.</p>
              <Button
                onClick={() => setOpenDialog(true)}
                className="mt-6 bg-[#0D9488] hover:bg-[#0F766E] rounded-none uppercase tracking-widest font-semibold text-white"
                data-testid="empty-log-shot-button"
              >
                <Plus className="w-4 h-4 mr-2" /> Log First Shot
              </Button>
            </div>
          )}
        </Card>

        {/* Right rail: stats */}
        <div className="col-span-1 flex flex-col gap-6">
          <StatCard icon={Flame} label="Current Streak" value={stats?.streak ?? 0} suffix="days" testid="stat-streak" />
          <StatCard icon={Layers} label="This Week" value={stats?.week_total ?? 0} suffix="shots" testid="stat-week-total" />
          <StatCard icon={TrendingUp} label="Most Used" value={stats?.most_used || "—"} testid="stat-most-used" small />
        </div>
      </div>

      {/* Restock Alerts — vials <7 days of therapy left */}
      <RestockAlerts vials={vials} peptides={peptides} thresholdDays={7} />

      {/* Training mentorship strip (mod-pinned threads) */}
      <div className="mt-6">
        <MentorshipStrip topic="training" label="Training Mentorship" />
      </div>

      {/* Peptide timers per compound */}
      {stats?.per_peptide?.length > 0 && (
        <div className="mt-6" data-testid="peptide-timers-section">
          <div className="flex items-baseline justify-between mb-4">
            <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500">
              // Per-Peptide Timers ({stats.per_peptide.length})
            </div>
            <Link
              to="/history"
              className="text-xs font-mono-num uppercase tracking-widest text-[#0D9488] hover:underline"
              data-testid="peptide-timers-view-history"
            >
              View history →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {stats.per_peptide.map((p) => (
              <PeptideTimerCard
                key={p.peptide_name}
                item={p}
                now={now}
                onQuickLog={(name) => { setQuickLogPeptide(name); setOpenDialog(true); }}
                onEndCountdown={handleEndCountdown}
              />
            ))}
          </div>
        </div>
      )}

      {/* Paused peptides — appears only when there are any inactive/no-followup entries */}
      {stats?.paused_peptides?.length > 0 && (
        <div className="mt-6" data-testid="paused-peptides-section">
          <div className="flex items-baseline justify-between mb-3">
            <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 flex items-center gap-2">
              <Pause className="w-3 h-3" />
              // Paused ({stats.paused_peptides.length})
            </div>
            <div className="text-[11px] font-mono-num text-slate-400 uppercase tracking-widest hidden sm:block">
              History kept · reactivate any time
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {stats.paused_peptides.map((p) => (
              <div
                key={p.peptide_name}
                className="bg-white border border-dashed border-slate-300 p-3 flex items-center justify-between gap-3"
                data-testid={`paused-peptide-${p.peptide_name}`}
              >
                <div className="min-w-0">
                  <div className="font-display font-black uppercase tracking-tight text-sm leading-tight truncate" title={p.peptide_name}>
                    {p.peptide_name}
                  </div>
                  <div className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest truncate">
                    {p.last_dose_amount}{p.last_dose_unit} · {p.reason}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleReactivate(p.peptide_name)}
                  className="shrink-0 h-8 px-3 rounded-md border border-teal-200 bg-teal-50 hover:bg-teal-100 text-teal-800 text-[10px] font-mono-num uppercase tracking-widest font-semibold flex items-center gap-1.5"
                  data-testid={`reactivate-peptide-${p.peptide_name}`}
                >
                  <PlayCircle className="w-3.5 h-3.5" />
                  Reactivate
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-6">
        <Card className="col-span-1 lg:col-span-3 bg-white border border-slate-200 rounded-none p-6 lg:p-8" data-testid="chart-card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-1">// 7-day Cadence</div>
              <div className="font-display font-black uppercase tracking-tight text-2xl">Shot Frequency</div>
            </div>
            <div className="flex items-center gap-2 text-slate-500 text-xs">
              <Clock className="w-3.5 h-3.5" />
              <span className="font-mono-num uppercase tracking-widest">Last 7 days</span>
            </div>
          </div>
          <ChartFrame className="h-[220px]">
            {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="blueFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0D9488" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#0D9488" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="day" stroke="#64748B" tick={{ fontSize: 11 }} />
                <YAxis stroke="#64748B" allowDecimals={false} tick={{ fontSize: 11 }} width={30} />
                <Tooltip />
                <Area type="monotone" dataKey="shots" stroke="#0D9488" strokeWidth={2} fill="url(#blueFill)" />
              </AreaChart>
            </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400 text-xs font-mono-num uppercase tracking-widest">
                No shots yet
              </div>
            )}
          </ChartFrame>
        </Card>

        <Card className="col-span-1 bg-white border border-slate-200 rounded-none p-6" data-testid="totals-card">
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-4">// Lifetime</div>
          <div className="font-display font-black text-6xl leading-none">{stats?.total_shots ?? 0}</div>
          <div className="text-slate-500 uppercase tracking-widest text-xs mt-3 font-mono-num">Total Shots</div>
          <div className="mt-6 pt-6 border-t border-slate-200">
            <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-2">// Compounds</div>
            <div className="font-display font-black text-3xl">{peptides.length}</div>
            <div className="text-slate-500 uppercase tracking-widest text-xs mt-2 font-mono-num">Available</div>
          </div>
        </Card>
      </div>

      <LogShotDialog
        open={openDialog}
        onOpenChange={(v) => { setOpenDialog(v); if (!v) setQuickLogPeptide(null); }}
        peptides={peptides}
        onSaved={load}
        initialPeptide={quickLogPeptide}
        shots={shots}
      />

      <OnboardingDialog open={user && !user.terms_accepted} onAccepted={refreshUser} />

      {/* Body weight + Quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-6">
        <Card className="col-span-1 bg-white border border-slate-200 rounded-none p-6" data-testid="body-weight-card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono-num text-[11px] uppercase tracking-[0.25em] text-slate-500">Body Weight</div>
            <Scale className="w-4 h-4 text-[#0D9488]" />
          </div>
          {weightEdit ? (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.1"
                autoFocus
                value={weightVal}
                onChange={(e) => setWeightVal(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && saveWeight()}
                className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num text-lg"
                data-testid="body-weight-input"
              />
              <Button size="icon" onClick={saveWeight} className="h-11 w-11 bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none" data-testid="body-weight-save">
                <Check className="w-4 h-4" />
              </Button>
              <Button size="icon" variant="outline" onClick={() => setWeightEdit(false)} className="h-11 w-11 border-slate-200 bg-transparent hover:bg-slate-50 rounded-none">
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div>
              <button
                type="button"
                onClick={() => { setWeightVal(user?.body_weight ? String(user.body_weight) : ""); setWeightEdit(true); }}
                className="text-left w-full group"
                data-testid="body-weight-edit"
              >
                <div className="font-display font-black text-5xl leading-none">
                  {user?.body_weight != null ? user.body_weight : "—"}
                </div>
                <div className="font-mono-num text-[11px] text-slate-500 uppercase tracking-widest mt-2 group-hover:text-[#0D9488] transition-colors">
                  Current {user?.weight_unit || "lb"} · tap to edit
                </div>
              </button>
              <Link to="/weight"
                className="mt-3 inline-flex items-center gap-1.5 font-mono-num text-[10px] uppercase tracking-widest text-slate-500 hover:text-[#0D9488] transition-colors border border-slate-200 hover:border-[#0D9488] px-2 py-1"
                data-testid="body-weight-log-link">
                <HistoryIcon className="w-3 h-3" /> Log daily weight & chart →
              </Link>
            </div>
          )}

          {/* Goal + delta section */}
          <div className="mt-5 pt-5 border-t border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono-num text-[11px] uppercase tracking-[0.25em] text-slate-500 flex items-center gap-1.5">
                <Target className="w-3 h-3" /> Goal
              </div>
            </div>
            {goalEdit ? (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.1"
                  autoFocus
                  value={goalVal}
                  onChange={(e) => setGoalVal(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveGoal()}
                  placeholder="Target"
                  className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num"
                  data-testid="goal-weight-input"
                />
                <Button size="icon" onClick={saveGoal} className="h-10 w-10 bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none" data-testid="goal-weight-save">
                  <Check className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="outline" onClick={() => setGoalEdit(false)} className="h-10 w-10 border-slate-200 bg-transparent hover:bg-slate-50 rounded-none">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setGoalVal(user?.goal_weight ? String(user.goal_weight) : ""); setGoalEdit(true); }}
                className="text-left w-full group"
                data-testid="goal-weight-edit"
              >
                <div className="font-display font-black text-3xl leading-none">
                  {user?.goal_weight != null ? user.goal_weight : "—"}
                </div>
                <div className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest mt-1 group-hover:text-[#0D9488] transition-colors">
                  {user?.weight_unit || "lb"} · tap to {user?.goal_weight != null ? "edit" : "set"}
                </div>
              </button>
            )}

            {/* Delta + progress */}
            {user?.body_weight != null && user?.goal_weight != null && (() => {
              const cw = user.body_weight;
              const gw = user.goal_weight;
              const diff = cw - gw;
              const losing = diff > 0; // need to lose
              const gaining = diff < 0; // need to gain
              const absDiff = Math.abs(diff);
              const reached = absDiff < 0.05;
              const unit = user?.weight_unit || "lb";
              return (
                <div className="mt-4 pt-4 border-t border-slate-200" data-testid="weight-delta">
                  <div className="flex items-baseline justify-between mb-1.5">
                    <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500">
                      {reached ? "// Goal reached" : losing ? "// To lose" : "// To gain"}
                    </div>
                    <div className={`font-mono-num text-[10px] uppercase tracking-widest ${reached ? "text-[#0D9488]" : "text-slate-500"}`}>
                      {reached ? "★" : losing ? "↓" : "↑"}
                    </div>
                  </div>
                  <div className={`font-display font-black text-4xl leading-none ${reached ? "text-[#0D9488]" : "text-slate-900"}`} data-testid="weight-delta-value">
                    {reached ? "0.0" : absDiff.toFixed(1)}
                  </div>
                  <div className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest mt-1">
                    {unit} {reached ? "on target" : "remaining"}
                  </div>
                </div>
              );
            })()}
          </div>
        </Card>

        <Card className="col-span-1 lg:col-span-3 bg-white border border-slate-200 rounded-none p-6" data-testid="quick-actions">
          <div className="font-mono-num text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-4">// Quick Actions</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <QuickAction to="/calculators" icon={Calculator} label="Calculate" testid="qa-calculator" />
            <QuickAction to="/inventory" icon={Package} label="Inventory" testid="qa-inventory" />
            <QuickAction to="/library" icon={BookOpen} label="Library" testid="qa-library" />
            <QuickAction to="/cycles" icon={Repeat} label="Cycles" testid="qa-cycles" />
          </div>
        </Card>
      </div>

      {/* Active cycles */}
      {activeCycles.length > 0 && (
        <div className="mt-6" data-testid="active-cycles-section">
          <div className="flex items-baseline justify-between mb-4">
            <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500">// Active Cycles ({activeCycles.length})</div>
            <Link to="/cycles" className="text-xs font-mono-num uppercase tracking-widest text-[#0D9488] hover:underline" data-testid="view-all-cycles">View all →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {activeCycles.slice(0, 3).map((c) => (
              <div key={c.id} className="bg-white border border-slate-200 p-4 hover:border-slate-300 transition-colors" data-testid={`active-cycle-${c.id}`}>
                <div className="font-mono-num text-[10px] text-[#0D9488] uppercase tracking-widest mb-1">// Active</div>
                <div className="font-display font-black text-xl uppercase tracking-tight">{c.peptide_name}</div>
                <div className="font-mono-num text-xs text-slate-500 mt-2">
                  {c.dose_mg ? `${c.dose_mg}mg` : ""} {c.frequency ? `· ${c.frequency}` : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ProductTour steps={TOUR_STEPS} open={tourOpen} onClose={closeTour} />
      {coachingEnabled && (
        <ClientOnboardingWizard
          open={onboardingOpen}
          initial={onboardingInitial}
          onClose={() => setOnboardingOpen(false)}
        />
      )}
    </AppLayout>
  );
}

function QuickAction({ to, icon: Icon, label, testid }) {
  return (
    <Link
      to={to}
      className="group border border-slate-200 hover:border-[#0D9488] bg-slate-50 p-4 flex flex-col items-start gap-3 transition-colors"
      data-testid={testid}
    >
      <div className="w-9 h-9 border border-slate-200 group-hover:border-[#0D9488] group-hover:bg-[#0D9488]/10 flex items-center justify-center transition-colors">
        <Icon className="w-4 h-4 text-slate-600 group-hover:text-[#0D9488] transition-colors" strokeWidth={2} />
      </div>
      <div className="font-display font-black uppercase tracking-tight text-lg leading-none">{label}</div>
    </Link>
  );
}

function PeptideTimerCard({ item, now, onQuickLog, onEndCountdown }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const target = new Date(item.next_dose_at).getTime();
  const takenAt = new Date(item.last_taken_at).getTime();
  const totalMs = (item.interval_hours || 24) * 3600000;
  const remaining = target - now;
  const elapsed = now - takenAt;
  const overdue = remaining <= 0;
  const abs = Math.abs(remaining);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const s = Math.floor((abs % 60000) / 1000);
  const pct = Math.max(0, Math.min(100, (elapsed / totalMs) * 100));
  const lastDose = `${item.last_dose_amount}${item.last_dose_unit}`;
  const ago = (() => {
    const d = now - takenAt;
    const hh = Math.floor(d / 3600000);
    const mm = Math.floor((d % 3600000) / 60000);
    if (hh <= 0 && mm <= 0) return "just now";
    if (hh <= 0) return `${mm}m ago`;
    return `${hh}h ${mm}m ago`;
  })();

  const confirmEnd = () => {
    setConfirmOpen(false);
    onEndCountdown?.(item.peptide_name);
  };

  return (
    <div
      className={`relative text-left bg-white border ${overdue ? "border-[#FF3B30]/50" : "border-slate-200"} p-4 flex flex-col gap-3 transition-colors group`}
      data-testid={`peptide-timer-${item.peptide_name}`}
    >
      {/* End Countdown mini-button (top-right, doesn't hijack the tap-to-log click) */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setConfirmOpen(true); }}
        title="End this countdown"
        aria-label={`End countdown for ${item.peptide_name}`}
        data-testid={`peptide-timer-end-${item.peptide_name}`}
        className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-[#FF3B30] hover:bg-red-50 transition-colors opacity-70 hover:opacity-100 z-10"
      >
        <StopCircle className="w-3.5 h-3.5" />
      </button>

      {/* Tap-to-log body (button-in-a-div so the icon-button above doesn't nest) */}
      <button
        type="button"
        onClick={() => onQuickLog(item.peptide_name)}
        className={`flex flex-col gap-3 -m-4 p-4 hover:${overdue ? "border-[#FF3B30]" : "border-[#0D9488]"} transition-colors`}
        style={{ background: "transparent" }}
      >
        <div className="flex items-baseline justify-between gap-2 pr-6">
          <div className="font-display font-black uppercase tracking-tight text-lg leading-tight truncate" title={item.peptide_name}>
            {item.peptide_name}
          </div>
          <span className={`font-mono-num text-[10px] uppercase tracking-widest ${overdue ? "text-[#FF3B30]" : "text-slate-500"}`}>
            {overdue ? "// overdue" : "// due in"}
          </span>
        </div>

        <div className={`font-mono-num font-black text-3xl leading-none tabular-nums ${overdue ? "text-[#FF3B30]" : "text-slate-900"}`} data-testid={`peptide-timer-value-${item.peptide_name}`}>
          {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
        </div>

        <div className="h-1 bg-slate-50">
          <div
            className={`h-full transition-[width] ${overdue ? "bg-[#FF3B30]" : "bg-[#0D9488]"}`}
            style={{ width: `${overdue ? 100 : pct}%` }}
          />
        </div>

        <div className="flex items-center justify-between gap-2 pt-1">
          <div className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest truncate">
            Last: {lastDose} · {ago}
          </div>
          <div className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest">
            {item.interval_hours}h
          </div>
        </div>

        <div className="font-mono-num text-[10px] text-slate-400 group-hover:text-[#0D9488] uppercase tracking-widest transition-colors">
          Tap to log next dose →
        </div>
      </button>

      {/* Confirmation dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-white border border-slate-200 rounded-xl" data-testid={`end-countdown-confirm-${item.peptide_name}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>End the {item.peptide_name} countdown?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed">
              We'll stop the next-dose timer and stop any overdue reminders for this peptide.
              <br /><br />
              <span className="text-slate-700 font-semibold">Your past shots, vials, and history will stay saved.</span> You can reactivate this peptide any time to start a new schedule.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`end-countdown-cancel-${item.peptide_name}`}>Keep tracking</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmEnd}
              className="bg-[#FF3B30] hover:bg-[#D62E24] text-white"
              data-testid={`end-countdown-confirm-btn-${item.peptide_name}`}
            >
              End countdown
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InfoLine({ label, value }) {
  return (
    <div>
      <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{label}</div>
      <div className="text-slate-900 font-medium truncate">{value}</div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, suffix, testid, small }) {
  return (
    <div className="bg-white border border-slate-200 rounded-none p-6 flex flex-col gap-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="font-mono-num text-[11px] uppercase tracking-[0.25em] text-slate-500">{label}</div>
        <Icon className="w-4 h-4 text-[#0D9488]" strokeWidth={2.2} />
      </div>
      <div className={`font-display font-black leading-none ${small ? "text-2xl" : "text-5xl"}`}>{value}</div>
      {suffix && <div className="font-mono-num text-[11px] uppercase tracking-widest text-slate-500">{suffix}</div>}
    </div>
  );
}
